import React from 'react';
import { Button, Space, Typography } from 'antd';
import { MinusOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { signupPalette, signupGradient } from './signupTheme';

const { Text } = Typography;

type Props = {
    value: number;
    max: number;
    onChange: (count: number) => void;
    onContinue: () => void;
};

/** Étape 3 — nombre de comptes enfants à créer. */
const StepChildren: React.FC<Props> = ({ value, max, onChange, onContinue }) => {
    const { t } = useTranslation();
    const clamp = (n: number) => Math.min(max, Math.max(1, n));
    const shortcuts = [1, 2, 3, 4].filter((n) => n <= max);

    return (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 24,
                    padding: '28px 16px',
                    borderRadius: 16,
                    border: `1px solid ${signupPalette.border}`,
                    background: signupPalette.background,
                }}
            >
                <Button
                    shape="circle"
                    size="large"
                    icon={<MinusOutlined />}
                    disabled={value <= 1}
                    onClick={() => onChange(clamp(value - 1))}
                    aria-label={t('signup.children.decrease', 'Un enfant de moins')}
                />
                <div style={{ textAlign: 'center', minWidth: 96 }}>
                    <div style={{ fontSize: 48, fontWeight: 700, lineHeight: 1, color: signupPalette.primary }}>{value}</div>
                    <Text style={{ color: signupPalette.textSecondary, fontSize: 14 }}>
                        <TeamOutlined style={{ marginInlineEnd: 6 }} />
                        {t('signup.children.unit', '{{count}} enfant', { count: value })}
                    </Text>
                </div>
                <Button
                    shape="circle"
                    size="large"
                    icon={<PlusOutlined />}
                    disabled={value >= max}
                    onClick={() => onChange(clamp(value + 1))}
                    aria-label={t('signup.children.increase', 'Un enfant de plus')}
                />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                {shortcuts.map((n) => (
                    <Button
                        key={n}
                        shape="round"
                        onClick={() => onChange(n)}
                        type={value === n ? 'primary' : 'default'}
                        style={value === n ? { border: 'none', background: signupGradient } : undefined}
                    >
                        {n}
                    </Button>
                ))}
            </div>

            <Text style={{ color: signupPalette.textSecondary, fontSize: 13, textAlign: 'center', display: 'block' }}>
                {t(
                    'signup.children.hint',
                    'Les comptes sont créés avec un prénom provisoire ; vous pourrez les renommer à tout moment.'
                )}
            </Text>

            <Button
                type="primary"
                size="large"
                block
                onClick={onContinue}
                style={{ height: 52, borderRadius: 12, border: 'none', background: signupGradient, fontWeight: 600, fontSize: 16 }}
            >
                {t('signup.actions.continue', 'Continuer')}
            </Button>
        </Space>
    );
};

export default StepChildren;
